/**
 * /app/reports — agency reports list (Task 041 §3).
 *
 * Wired against `list-report-jobs[?status=]`, `get-report-job?id=`,
 * `list-report-artifacts?report_job_id=`. The endpoint safely supports
 * an omitted `client_id` (Task 033 §1) — RLS handles per-row scoping
 * (leadership sees org-wide, account managers see assigned clients
 * plus org-wide finance summaries gated by `report.finance.read`).
 *
 * Each row surfaces the owning client (or "على مستوى الوكالة" when
 * `client_id` is null) so the agency-level view stays useful without
 * forcing the user to drill in.
 *
 * Download / re-run / cancel remain deferred — the artifact download
 * endpoint hasn't shipped yet (`docs/frontend/035 §6.8`).
 */

import { useEffect, useMemo, useState } from 'react';
import { isApiError } from '../api/client';
import { useClientScope } from '../context/ClientScopeProvider';
import { getReportJob, listReportArtifacts, listReportJobs } from '../api/endpoints';
import type { ReportArtifactRow, ReportJobRow, ReportJobStatus, ReportType } from '../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../components/states';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly jobs: ReadonlyArray<ReportJobRow> }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly job: ReportJobRow;
      readonly artifacts: ReadonlyArray<ReportArtifactRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const STATUS_FILTERS: ReadonlyArray<{
  readonly value: ReportJobStatus | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'الكل' },
  { value: 'queued', label: 'في الطابور' },
  { value: 'processing', label: 'قيد المعالجة' },
  { value: 'succeeded', label: 'ناجحة' },
  { value: 'failed', label: 'فاشلة' },
  { value: 'cancelled', label: 'ملغاة' },
];

export function AgencyReportsPage() {
  const { clients, status: clientScopeStatus } = useClientScope();
  const [statusFilter, setStatusFilter] = useState<ReportJobStatus | 'all'>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) map.set(c.id, c.name);
    return map;
  }, [clients]);

  useEffect(() => {
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listReportJobs({
      status: statusFilter === 'all' ? undefined : statusFilter,
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', jobs: data.report_jobs });
        if (data.report_jobs.length > 0 && data.report_jobs[0]) {
          setSelectedId((prev) =>
            prev && data.report_jobs.some((j) => j.id === prev) ? prev : data.report_jobs[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [statusFilter, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getReportJob(selectedId, { signal: ctrl.signal }),
      listReportArtifacts(selectedId, { signal: ctrl.signal }),
    ])
      .then(([jobData, artifactsData]) => {
        if (ctrl.signal.aborted) return;
        setDetail({
          status: 'ready',
          job: jobData.report_job,
          artifacts: artifactsData.report_artifacts,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId]);

  const formatClient = (clientId: string | null): string => {
    if (!clientId) return 'على مستوى الوكالة';
    const cached = clientNameById.get(clientId);
    if (cached) return cached;
    // While the client list is still loading, render a neutral placeholder
    // rather than misleading "unknown client" text (Codex P3).
    if (clientScopeStatus === 'loading' || clientScopeStatus === 'idle') return '—';
    // Status is settled but the id is genuinely outside the visible set —
    // surface a UUID prefix so the user has a traceable identifier.
    return `عميل غير متاح · ${clientId.slice(0, 8)}`;
  };

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          التقارير <em className="serif italic">على مستوى الوكالة</em>
        </h1>
        <p className="body" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          كل طلبات التقارير المرئية لك. التنزيل سيُفعّل بعد شحن نقطة إصدار الروابط الموقّتة.
        </p>
      </div>

      <div className="toolbar-row">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={statusFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginInlineStart: 'auto' }}>
          <button type="button" className="btn-coming-soon" disabled>
            تقرير جديد (قريباً)
          </button>
        </span>
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل التقارير…" /> : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل التقارير"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {list.status === 'ready' ? (
        list.jobs.length === 0 ? (
          <EmptyState
            title="لا توجد تقارير بعد"
            description="ستظهر هنا كل التقارير على مستوى الوكالة بمجرد إنشاء أول طلب."
          />
        ) : (
          <div className="workspace-split">
            <aside className="workspace-list">
              <div className="sec-title">
                <h2>طلبات التقارير</h2>
                <span className="meta">{list.jobs.length}</span>
              </div>
              <ul className="row-list">
                {list.jobs.map((j) => (
                  <li key={j.id}>
                    <button
                      type="button"
                      className={j.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                      onClick={() => setSelectedId(j.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <strong style={{ fontSize: 13 }}>{j.title}</strong>
                        <span className={`pill pill-${reportStatusVariant(j.status)}`}>
                          {reportStatusLabel(j.status)}
                        </span>
                      </div>
                      <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                        {reportTypeLabel(j.report_type)} · {formatClient(j.client_id)}
                      </div>
                      <div className="tiny" style={{ marginTop: 2, color: 'var(--muted)' }}>
                        {new Date(j.requested_at).toLocaleDateString('ar-SA')}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
            <section className="workspace-detail">
              <ReportDetail state={detail} formatClient={formatClient} />
            </section>
          </div>
        )
      ) : null}
    </div>
  );
}

function ReportDetail({
  state,
  formatClient,
}: {
  readonly state: DetailState;
  readonly formatClient: (clientId: string | null) => string;
}) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر تقريراً لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل التقرير…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="التقرير غير متاح" description="ربما تم أرشفته." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل التقرير" error={state.error} />;

  const { job, artifacts } = state;
  const clientLabel = formatClient(job.client_id);
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{job.title}</h2>
          <span className={`pill pill-${reportStatusVariant(job.status)}`}>
            {reportStatusLabel(job.status)}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="العميل" value={clientLabel} />
          <Field label="نوع التقرير" value={reportTypeLabel(job.report_type)} />
          <Field label="اللغة" value={job.locale} />
          <Field label="المنطقة الزمنية" value={job.timezone} />
          <Field
            label="الفترة"
            value={
              job.period_start && job.period_end ? `${job.period_start} → ${job.period_end}` : null
            }
          />
          <Field label="طلب في" value={new Date(job.requested_at).toLocaleString('ar-SA')} />
          <Field
            label="بدأ"
            value={job.started_at ? new Date(job.started_at).toLocaleString('ar-SA') : null}
          />
          <Field
            label="اكتمل"
            value={job.completed_at ? new Date(job.completed_at).toLocaleString('ar-SA') : null}
          />
          <Field label="رمز الفشل" value={job.failure_code} />
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <button type="button" className="btn-coming-soon" disabled>
            إعادة تشغيل (قريباً)
          </button>
          <button type="button" className="btn-coming-soon" disabled>
            إلغاء (قريباً)
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الملفات الناتجة</h2>
          <span className="meta">{artifacts.length}</span>
        </div>
        {artifacts.length === 0 ? (
          <EmptyState
            title="لا توجد ملفات بعد"
            description="ستظهر هنا الملفات الناتجة (PDF / CSV / HTML) بمجرد اكتمال التقرير."
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>اسم الملف</th>
                <th>الصيغة</th>
                <th>الحجم</th>
                <th>تم الإنشاء</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {artifacts.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="name">{a.file_name ?? '—'}</span>
                    {a.checksum_sha256 ? (
                      <div
                        className="sub"
                        style={{ fontFamily: 'var(--font-mono)', direction: 'ltr' }}
                      >
                        sha256: {a.checksum_sha256.slice(0, 12)}…
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className="pill pill-info">{a.artifact_format.toUpperCase()}</span>
                  </td>
                  <td className="num">{a.byte_size ? formatBytes(a.byte_size) : '—'}</td>
                  <td>{new Date(a.created_at).toLocaleDateString('ar-SA')}</td>
                  <td>
                    <button type="button" className="btn-coming-soon" disabled>
                      تنزيل (قريباً)
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 10 }}>
          ملاحظة: التنزيل سيُفعّل بعد شحن نقطة <code>download-report-artifact</code> الخاصة بإصدار
          روابط تخزين موقّتة.
        </p>
      </div>
    </div>
  );
}

function reportStatusLabel(status: ReportJobStatus): string {
  switch (status) {
    case 'queued':
      return 'في الطابور';
    case 'processing':
      return 'قيد المعالجة';
    case 'succeeded':
      return 'ناجحة';
    case 'failed':
      return 'فاشلة';
    case 'cancelled':
      return 'ملغاة';
    default:
      return status;
  }
}

function reportStatusVariant(status: ReportJobStatus): string {
  switch (status) {
    case 'queued':
      return 'draft';
    case 'processing':
      return 'review';
    case 'succeeded':
      return 'active';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'archived';
    default:
      return 'info';
  }
}

function reportTypeLabel(type: ReportType): string {
  switch (type) {
    case 'client_summary':
      return 'ملخص العميل';
    case 'initiative_progress':
      return 'تقدم المبادرة';
    case 'content_performance':
      return 'أداء المحتوى';
    case 'publishing_performance':
      return 'أداء النشر';
    case 'paid_media_performance':
      return 'أداء الإعلانات';
    case 'crm_pipeline':
      return 'قمع المبيعات';
    case 'operations_overview':
      return 'لمحة عمليات';
    case 'finance_summary':
      return 'ملخص مالي';
    case 'custom':
      return 'تقرير مخصص';
    default:
      return type;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}
